import { Button, XolaLogoSimple } from "@xola/ui-kit";
import React from "react";
import { buildEnvironmentBadge } from "./environmentBadge";
import { createAuthorizationRedirect } from "@/auth/oauthSessionHelper";
import { signOut } from "@/auth/signOut";

// The same asset the Xola login page uses, so the handoff does not repaint the background.
const LOGIN_BACKGROUND = "https://files.xola.com/x2/images/login/x2-34fc260271ca80160c61777846784611.jpeg";

interface LoginCardProps {
    children: React.ReactNode;
}

/** Mirrors the card in `xola/templates/oauth/login.html.twig`, which this screen hands over to. */
const LoginCard = ({ children }: LoginCardProps) => {
    return (
        <div
            className="flex min-h-screen flex-col items-center justify-center bg-cover bg-center"
            // The photo is a remote asset, so its URL cannot come from a Tailwind class.
            style={{ backgroundImage: `url(${LOGIN_BACKGROUND})` }}
        >
            <div className="w-85 rounded-lg bg-white px-9 py-10">
                <div className="mb-12 space-y-4 text-center md:mb-20">
                    <XolaLogoSimple className="mx-auto h-10 w-auto text-black md:h-12" />
                    {buildEnvironmentBadge()}
                </div>
                {children}
            </div>
        </div>
    );
};

const FieldPlaceholder = () => {
    return (
        <div className="mb-[18px]">
            <div className="mb-1.5 h-3 w-16 rounded-sm bg-gray-light" />
            <div className="h-[38px] rounded-sm bg-gray-lighter" />
        </div>
    );
};

/** Shown while it is still unknown whether this browser has a session, so no dashboard is loading. */
export const SignInSkeleton = () => {
    return (
        <LoginCard>
            <div className="animate-pulse">
                <FieldPlaceholder />
                <FieldPlaceholder />
                <div className="mb-[18px] flex items-center gap-2">
                    <div className="h-4 w-4 rounded-sm bg-gray-light" />
                    <div className="h-3 w-24 rounded-sm bg-gray-light" />
                </div>
                <div className="h-10 rounded-sm bg-gray-light" />
                <div className="mx-auto mt-[18px] h-3 w-36 rounded-sm bg-gray-lighter" />
            </div>
        </LoginCard>
    );
};

const handleRetry = async () => {
    window.location.assign(await createAuthorizationRedirect());
};

// A button, not an auto-redirect: `/api/authorize` needs no interaction, so failures would loop.
export const SignInFailedScreen = () => {
    return (
        <LoginCard>
            <p className="mb-4 text-center text-sm text-gray-darker">We could not complete your sign in.</p>
            <Button className="w-full" onClick={handleRetry}>
                Try again
            </Button>
        </LoginCard>
    );
};

export const IdleLogoutNotice = () => (
    <div className="rounded bg-blue-lighter p-3 text-sm text-gray-darker">
        Session expired due to inactivity. Please log in again to continue.
    </div>
);

const handleSignOut = async () => signOut();

interface AccountErrorScreenProps {
    message?: string;
}

// Log out, not retry: signing in as the same user would land here again.
export const AccountErrorScreen = ({ message }: AccountErrorScreenProps) => {
    return (
        <LoginCard>
            <p className="mb-4 text-center text-sm text-gray-darker">{message ?? "We could not load your account."}</p>
            <Button className="w-full" onClick={handleSignOut}>
                Log out
            </Button>
        </LoginCard>
    );
};
